import React, { useState, useEffect } from 'react';

import { useDebouncedCallback, useInViewport } from '@good/ui/hooks';
import { Box } from '@good/ui/core';
import { InfiniteScrollLoadingSkeleton } from './infinite-scroll-loading-skeleton';
import { InfiniteScrollEnd } from './infinite-scroll-end';

type InfiniteScrollProps = {
  hasMore: boolean;
  loadMore: (() => Promise<void>) | (() => void);
  customLoader?: React.ReactNode;
  children?: React.ReactNode;
  asTableRow?: boolean;
  triggerOnLoaderVisible?: boolean;
  loaderProps?: {
    h?: number;
    rows?: number;
  };
  initialPage?: boolean;
};

export const InfiniteScroll = ({
  hasMore,
  loadMore,
  children,
  asTableRow,
  customLoader,
  loaderProps,
  triggerOnLoaderVisible,
  initialPage,
}: InfiniteScrollProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { ref, inViewport } = useInViewport();

  const load = useDebouncedCallback(async () => {
    if (!inViewport) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      await loadMore();
    } finally {
      setIsLoading(false);
    }
  }, 100);

  useEffect(() => {
    const loadMore = inViewport && hasMore && !isLoading && !initialPage;
    if (loadMore) {
      void load();
    }
  }, [inViewport, isLoading, hasMore, load, initialPage]);

  return (
    <>
      {children}
      {triggerOnLoaderVisible && <Box component={asTableRow ? 'tr' : 'div'} ref={ref} h={1} />}
      {hasMore ? (
        <>
          {customLoader ?? (
            <InfiniteScrollLoadingSkeleton loading={isLoading} asTableRow={asTableRow} {...loaderProps} />
          )}
        </>
      ) : (
        <InfiniteScrollEnd asTableRow={asTableRow} />
      )}
      {!triggerOnLoaderVisible && <Box component={asTableRow ? 'tr' : 'div'} ref={ref} h={1} />}
    </>
  );
};
